import { formatDate } from "@/lib/utils";
import React from "react";
import DOMPurify from "dompurify";
import PropTypes from "prop-types";
import { Briefcase, Plus, Wrench, UserCog, Clock, DollarSign, Users } from "lucide-react";
import Modal from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

const LaborViewModal = ({
  show,
  onHide,
  selectedRepairJob,
  laborEntries,
  currencySymbol,
  repairJobs,
  handleAddLabor,
}) => {
  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2">
          <span>Labor History</span>
          <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {selectedRepairJob?.item_description}
          </span>
        </div>
      }
      icon={Briefcase}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex justify-end w-full">
          <Button variant="outline" onClick={onHide}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {laborEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-xl bg-muted/30">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Wrench className="h-8 w-8 text-muted-foreground" />
            </div>
            <h5 className="text-lg font-semibold">No Labor History Found</h5>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-6">
              No labor entries have been recorded for this repair job yet.
            </p>
            {(selectedRepairJob?.status?.value || selectedRepairJob?.status) !==
              "completed" && (
              <Button
                onClick={() => {
                  onHide();
                  handleAddLabor(selectedRepairJob);
                }}
                className="gap-2"
              >
                <Plus size={16} /> Add First Activity
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h6 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                Activity List ({laborEntries.length})
              </h6>
              {(selectedRepairJob?.status?.value ||
                selectedRepairJob?.status) !== "completed" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-2"
                  onClick={() => {
                    onHide();
                    handleAddLabor(
                      repairJobs.find((job) => job.id === selectedRepairJob.id),
                    );
                  }}
                >
                  <Plus size={14} /> Add Activity
                </Button>
              )}
            </div>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {laborEntries.map((entry, index) => {
                  const startTime = new Date(entry.start_time);
                  const endTime = entry.end_time
                    ? new Date(entry.end_time)
                    : null;
                  const diffMs = endTime ? endTime - startTime : 0;
                  const diffHours = diffMs / (1000 * 60 * 60);

                  return (
                    <Card key={entry.id || index} className="overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b">
                        <div className="flex items-center gap-2">
                          <UserCog className="h-4 w-4 text-primary" />
                          <span className="font-semibold text-sm">
                            {entry.technician?.full_name ||
                              "Unknown Technician"}
                          </span>
                          <span className="text-[10px] text-muted-foreground uppercase">
                            - Activity #{index + 1}
                          </span>
                        </div>
                        <Badge variant="secondary" className="font-mono">
                          {currencySymbol}
                          {(
                            diffHours * parseFloat(entry.hourly_rate || 0)
                          ).toFixed(2)}
                        </Badge>
                      </div>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="space-y-1">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                              Start Time
                            </span>
                            <span className="text-sm">
                              {formatDate(startTime)}{" "}
                              {startTime.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                              End Time
                            </span>
                            <span className="text-sm">
                              {endTime ? (
                                <>
                                  {formatDate(endTime)}{" "}
                                  {endTime.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] py-0 h-4 animate-pulse bg-green-50 text-green-700 border-green-200"
                                >
                                  In Progress
                                </Badge>
                              )}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                              Hours
                            </span>
                            <span className="text-sm font-mono">
                              {diffHours > 0 ? diffHours.toFixed(2) : "0.00"} h
                            </span>
                          </div>
                          <div className="space-y-1">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block">
                              Rate
                            </span>
                            <span className="text-sm font-mono">
                              {currencySymbol}
                              {parseFloat(entry.hourly_rate || 0).toFixed(2)}
                              /h
                            </span>
                          </div>
                        </div>

                        {entry.description && (
                          <div className="mt-4 pt-3 border-t">
                            <span className="text-[10px] font-bold uppercase text-muted-foreground block mb-2">
                              Technician Notes
                            </span>
                            <div
                              className="text-sm text-balance leading-relaxed bg-muted/20 p-2 rounded italic border-l-2 border-primary"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(entry.description || ""),
                              }}
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>

            <Card className="bg-primary text-primary-foreground border-none">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-1.5 opacity-80">
                      <Briefcase size={14} />
                      <span className="text-[10px] font-bold uppercase">
                        Total Entries
                      </span>
                    </div>
                    <p className="text-xl font-bold">{laborEntries.length}</p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-1.5 opacity-80">
                      <Clock size={14} />
                      <span className="text-[10px] font-bold uppercase">
                        Total Hours
                      </span>
                    </div>
                    <p className="text-xl font-bold">
                      {laborEntries
                        .reduce((total, entry) => {
                          const start = new Date(entry.start_time);
                          const end = entry.end_time
                            ? new Date(entry.end_time)
                            : null;
                          const diff = end ? end - start : 0;
                          const hours = diff / (1000 * 60 * 60);
                          return total + (hours > 0 ? hours : 0);
                        }, 0)
                        .toFixed(2)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-1.5 opacity-80">
                      <Users size={14} />
                      <span className="text-[10px] font-bold uppercase">
                        Staff Count
                      </span>
                    </div>
                    <p className="text-xl font-bold">
                      {new Set(laborEntries.map((e) => e.technician_id)).size}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-1.5 opacity-80">
                      <DollarSign size={14} />
                      <span className="text-[10px] font-bold uppercase">
                        Total Cost
                      </span>
                    </div>
                    <p className="text-xl font-bold">
                      {currencySymbol}
                      {laborEntries
                        .reduce((total, entry) => {
                          const startTime = new Date(entry.start_time);
                          const endTime = entry.end_time
                            ? new Date(entry.end_time)
                            : null;
                          const diffMs = endTime ? endTime - startTime : 0;
                          const diffHours = diffMs / (1000 * 60 * 60);
                          const hours = diffHours > 0 ? diffHours : 0;
                          const cost =
                            hours * parseFloat(entry.hourly_rate || 0);
                          return total + cost;
                        }, 0)
                        .toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Modal>
  );
};

LaborViewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedRepairJob: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    item_description: PropTypes.string,
    status: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
  }),
  laborEntries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      technician: PropTypes.shape({
        full_name: PropTypes.string,
      }),
      hourly_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      start_time: PropTypes.string,
      end_time: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  currencySymbol: PropTypes.string,
  repairJobs: PropTypes.array,
  handleAddLabor: PropTypes.func.isRequired,
};

export default LaborViewModal;
